import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactSVG } from "react-svg";
import { Button, Checkbox, Dimmer, Grid, Header, Icon, Loader, Menu, Table, Pagination, PaginationProps, Dropdown, DropdownProps, Input } from 'semantic-ui-react';
import { Chart, ReactGoogleChartEvent } from "react-google-charts";
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { startOfYear, addDays, addWeeks } from 'date-fns';
import _ from 'lodash';
import { debounce } from 'lodash';
import AppContext from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';
import { ReportsModel, ReportsDataModel, DataGridModel, IdNameViewModel } from '../../types/ServerDTO';
import DocumentDownload from "../../assets/images/document-download.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import { utils } from "../../helpers/utils";
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

let mouseOverListenerHandler = {};
let mouseOutListenerHandler = {};
let clickListenerHandler = {};
export const Reports: React.FC = () => {
  const { userInfo } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [drilldownRow, setDrilldownRow] = useState<number>();
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [chartHeight, setChartHeight] = useState<string>('350px');
  const [isDrilldownData, setIsDrilldownData] = useState<boolean>(false);
  const [isGraphDataAvailable, setIsGraphDataAvailable] = useState<boolean>();
  const [formValues, setFormValues] = useState<ReportsModel>({} as ReportsModel);
  const [reportData, setReportData] = useState<ReportsDataModel>({} as ReportsDataModel);
  const [graphData, setGraphData] = useState<any>();
  const [graphOptions, setGraphOptions] = useState<any>({});
  const [postData, setPostData] = useState<DataGridModel>();
  const [triggerEffect, setTriggerEffect] = useState(false);
  const [columns, setColumns] = useState({} as any);
  const [dataGrid, setDataGrid] = useState({} as any);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  const navigate = useNavigate();

  const dateRangeId = 4;
  const reportTagId = 4;
  const reportAssignedToId = 6;
  const reportTypeNameArr: any = {
    1: 'caseType',
    2: 'status',
    3: 'source',
    4: 'tag',
    5: 'neighborhood',
    6: 'assignedTo',
    7: 'office',
  };
  const todayDate = new Date();
  const yesterdayDate = addDays(todayDate, -1);
  const dateRanges: any = {
    1: {
      fromDate: startOfYear(todayDate),
      toDate: todayDate
    },
    2: {
      fromDate: addDays(todayDate, -30),
      toDate: yesterdayDate
    },
    3: {
      fromDate: addWeeks(todayDate, -1),
      toDate: yesterdayDate
    },
    4: {
      fromDate: formValues.fromDate,
      toDate: formValues.toDate
    }
  };

  const logInReportColumns = [
    { field: 'office', header: 'Office', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.office, className: 'normal-text' },
    { field: 'user', header: 'User', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.user },
    { field: 'email', header: 'Email', sortable: true, headerStyle: { width: '40%' }, accessor: (row: any) => row.email },
    { field: 'loginTime', header: 'Login Time', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.loginTime },
  ];

  const userActivityColumns = [
    { field: 'office', header: 'Office', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.office, className: 'normal-text' },
    { field: 'user', header: 'User', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.user },
    { field: 'totalCasesCreated', header: 'Total Cases Created', sortable: true, headerStyle: { width: '40%' }, accessor: (row: any) => row.totalCasesCreated },
    { field: 'totalCasesClosed', header: 'Total Cases Closed', sortable: true, headerStyle: { width: '20%' }, accessor: (row: any) => row.totalCasesClosed },
  ];

  useEffect(() => {
    document.title = 'Reports - Adjure';
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (userInfo !== undefined) {
      fetchData();
    }
  }, [userInfo]);

  useEffect(() => {
    const fetchReportPageData = async () => {
      try {
        setIsLoading(true);
        let apiUrl = '';
        if (formValues.reportTypeId === 8) {
          apiUrl = '/Report/LoginUserDataGrid';
        } else if (formValues.reportTypeId === 9) {
          apiUrl = '/Report/UserActivityDataGrid';
        }
        await axiosInstance.post(apiUrl, postData).then(({ data, status }) => {
          if (status === 200) {
            setDataGrid(data);
            setIsLoading(false);
          }
        })
          .catch((error) => {
            setIsLoading(false);
            console.log(error);
          });
      } catch (error) {
        setIsLoading(false);
        console.log(error);
      }
    }

    if (triggerEffect) {
      setTriggerEffect(false);
      fetchReportPageData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [postData, triggerEffect]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get(`/Report/GetReportRequest`).then(({ data, status }) => {
        if (status === 200) {
          data.reportTypeId = 1;
          data.rangeTypeId = 1;
          if (userInfo?.IsAllOffices === "True" && [1, 2, 3].indexOf(data.reportTypeId) !== -1) {
            data.includeBreakDown = true;
          }
          const reportsFilter = localStorage.getItem('reportsFilter');
          if (reportsFilter !== null) {
            const reportsFilterJSON = JSON.parse(reportsFilter);
            data.reportTypeId = reportsFilterJSON.reportTypeId;
            data.rangeTypeId = reportsFilterJSON.rangeTypeId;
            data.fromDate = reportsFilterJSON.fromDate;
            data.toDate = reportsFilterJSON.toDate;
            data.includeBreakDown = reportsFilterJSON.includeBreakDown;
            localStorage.removeItem('reportsFilter');
          }
          setFormValues(data);
          fetchReportData(data);
        }
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const fetchReportData = async (overwritePayload: ReportsModel) => {
    try {
      setIsLoading(true);
      const payload = {} as ReportsModel;
      if (overwritePayload !== undefined) {
        Object.assign(payload, overwritePayload);
      }
      payload.masterData = null;
      await axiosInstance.post(`/Report/GetReport`, payload).then(({ data, status }) => {
        if (status === 200) {
          if (overwritePayload.reportTypeId <= 7) {
            const tmpData = data.graphReportResponse;
            setReportData(tmpData);
            const newData = [];
            if (tmpData.graphData.length > 0 && tmpData.graphData[0].breakdown.length > 0 && [1, 2, 3].indexOf(payload.reportTypeId) === -1) {
              const colors: any = [];
              const legendData = [tmpData.title];
              tmpData.legends.map((legend: any) => {
                legendData.push(legend.name);
                colors.push(legend.color);
                return true;
              });
              newData.push(legendData);
              tmpData.graphData.map((graph: any) => {
                const breakdownData = [graph.name];
                tmpData.legends.map((legend: any) => {
                  let graphValue = 0;
                  graph.breakdown.map((graph: any) => {
                    if (legend.name === graph.name) {
                      graphValue = graph.value;
                    }
                    return true;
                  });
                  breakdownData.push(graphValue);
                  return true;
                });
                newData.push(breakdownData);
                return true;
              });
              setIsGraphDataAvailable(true);
              setGraphOptions({
                'animation': {
                  startup: true,
                  easing: 'inAndOut',
                  duration: 300
                },
                isStacked: overwritePayload.reportTypeId === 7 ? false : true,
                colors,
                hAxis: { minValue: 0, format: '0' },
                chartArea: { top: 20, bottom: 20 },
                fontSize: 14
              });
            } else {
              if (tmpData.graphData.length > 0) {
                if (payload.reportTypeId === 2) {
                  newData.push([tmpData.title, 'Count', { role: "style" }]);
                } else {
                  newData.push([tmpData.title, 'Count']);
                }
                tmpData.graphData.map((graph: any, graphDataIndex: number) => {
                  if (payload.reportTypeId === 2) {
                    newData.push([graph.name, graph.value, tmpData.legends[graphDataIndex].color]);
                  } else {
                    newData.push([graph.name, graph.value]);
                  }
                  return true;
                });
                setIsGraphDataAvailable(true);
              } else {
                setIsGraphDataAvailable(false);
              }
              let graphOptions: any = {
                'animation': {
                  startup: true,
                  easing: 'inAndOut',
                  duration: 300
                },
                legend: { position: 'none' },
                isStacked: false,
                hAxis: { minValue: 0, format: '0' },
                chartArea: { top: 20, bottom: 20 },
                fontSize: 14
              };
              if ([1, 2, 3].indexOf(payload.reportTypeId) === -1 && tmpData.legends.length > 0) {
                const colors: any = [];
                tmpData.legends.map((legend: any) => {
                  colors.push(legend.color);
                  return true;
                });
                graphOptions.colors = colors;
              }
              if (!isDrilldownData && userInfo?.IsAllOffices === "True" && [1, 2, 3].indexOf(payload.reportTypeId) !== -1) {
                graphOptions.vAxis = { textStyle: { color: "#7152f6", underline: true } };
              }
              setGraphOptions(graphOptions);
            }
            let tmpChartHeight = newData.length * 40;
            if (tmpChartHeight < 350) {
              tmpChartHeight = 350;
            }
            setChartHeight(`${tmpChartHeight}px`);
            setGraphData(newData);
          } else {
            if (overwritePayload.reportTypeId === 8) {
              setColumns(logInReportColumns);
              const tmpPostData: any = {
                fromDate: data.loginUserResponse.fromDate,
                toDate: data.loginUserResponse.toDate,
                officeId: data.loginUserResponse.officeId,
                searchText: data.loginUserResponse.searchText,
                pagination: data.loginUserResponse.pagination,
                master: data.loginUserResponse.master,
              }
              setPostData(tmpPostData);
              setDataGrid(data.loginUserResponse);
            } else if (overwritePayload.reportTypeId === 9) {
              setColumns(userActivityColumns);
              const tmpPostData: any = {
                fromDate: data.userActivityResponse.fromDate,
                toDate: data.userActivityResponse.toDate,
                officeId: data.userActivityResponse.officeId,
                searchText: data.userActivityResponse.searchText,
                pagination: data.userActivityResponse.pagination,
                master: data.userActivityResponse.master,
              }
              setPostData(tmpPostData);
              setDataGrid(data.userActivityResponse);
            }
          }
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const revertDrilldownChart = () => {
    const newData = [];
    const data = reportData;
    if (formValues.reportTypeId === 2) {
      newData.push([data.title, 'Count', { role: "style" }]);
    } else {
      newData.push([data.title, 'Count']);
    }
    data.graphData.map((graph: any, graphDataIndex: number) => {
      if (formValues.reportTypeId === 2) {
        newData.push([graph.name, graph.value, data.legends[graphDataIndex].color]);
      } else {
        newData.push([graph.name, graph.value]);
      }
      return true;
    });
    setIsGraphDataAvailable(true);
    let graphOptions: any = {
      'animation': {
        startup: true,
        easing: 'inAndOut',
        duration: 300
      },
      legend: { position: 'none' },
      isStacked: false,
      hAxis: { minValue: 0, format: '0' },
      vAxis: { textStyle: { color: "#7152f6", underline: true } },
      chartArea: { top: 20, bottom: 20 },
      fontSize: 14
    };
    setGraphOptions(graphOptions);
    setGraphData(newData);
    setIsDrilldownData(false);
    setDrilldownTitle('');
    setDrilldownRow(undefined);
  }

  const handleReportType = (id: number) => {
    let includeBreakDown = false;
    if (id === reportAssignedToId || (userInfo?.IsAllOffices === "True" && [1, 2, 3].indexOf(id) !== -1)) {
      includeBreakDown = true;
    }
    setIsDrilldownData(false);
    setDrilldownTitle('');
    setDrilldownRow(undefined);
    setFormValues({ ...formValues, reportTypeId: id, includeBreakDown });
    fetchReportData({ ...formValues, reportTypeId: id, includeBreakDown });
  };

  const handleRangeType = (id: number) => {
    setIsDrilldownData(false);
    setDrilldownTitle('');
    setDrilldownRow(undefined);
    setFormValues({ ...formValues, rangeTypeId: id, fromDate: null, toDate: null });
    fetchReportData({ ...formValues, rangeTypeId: id, fromDate: null, toDate: null });
  };

  const handleSupport = (checked: boolean) => {
    setIsDrilldownData(false);
    setDrilldownTitle('');
    setDrilldownRow(undefined);
    setFormValues({ ...formValues, includeBreakDown: checked });
    fetchReportData({ ...formValues, includeBreakDown: checked });
  };

  const handleDateChange = (date: any, field: any) => {
    setIsDrilldownData(false);
    setDrilldownTitle('');
    setDrilldownRow(undefined);
    const selectedDate = date.value ? new Date(date.value) : undefined;
    setFormValues({ ...formValues, [field]: selectedDate });
    fetchReportData({ ...formValues, [field]: selectedDate });
  };

  const chartEvents: ReactGoogleChartEvent[] = [
    {
      eventName: 'select',
      callback({ chartWrapper }: any): void {
        if (!isDrilldownData && userInfo?.IsAllOffices === "True" && [1, 2, 3].indexOf(formValues.reportTypeId) !== -1) {
          const selectedRowColumn = chartWrapper.getChart().getSelection()[0];
          const data = reportData.graphData[selectedRowColumn.row];
          const newData = [];
          if (formValues.reportTypeId === 2) {
            newData.push([data.name, 'Count', { role: "style" }]);
          } else {
            newData.push([data.name, 'Count']);
          }
          data.breakdown.map((graph: any) => {
            if (formValues.reportTypeId === 2) {
              newData.push([graph.name, graph.value, reportData.legends[selectedRowColumn.row].color]);
            } else {
              newData.push([graph.name, graph.value]);
            }
            return true;
          });
          setIsGraphDataAvailable(true);
          setGraphOptions({
            'animation': {
              startup: true,
              easing: 'inAndOut',
              duration: 300
            },
            legend: { position: 'none' },
            hAxis: { minValue: 0, format: '0' },
            chartArea: { top: 20, bottom: 20 },
            fontSize: 14
          });
          setGraphData(newData);
          setIsDrilldownData(true);
          setDrilldownTitle(data.name);
          setDrilldownRow(selectedRowColumn.row);
        } else {
          const reportTypeName: string = reportTypeNameArr[formValues.reportTypeId];
          const selectedRowColumn = chartWrapper.getChart().getSelection()[0];
          let selectedRow = selectedRowColumn.row;
          let selectedColumn = selectedRowColumn.column - 1;
          if (isDrilldownData) {
            selectedRow = drilldownRow;
            selectedColumn = selectedRowColumn.row;
          }
          navigateWithFilter(reportTypeName, selectedRow, selectedColumn);
        }
      }
    },
    {
      eventName: 'ready',
      callback({ chartWrapper, google }): void {
        const chart = chartWrapper.getChart();
        const onMouseOverHandler = (e: Event) => {
          if (document.getElementById('reactgooglegraph-1')) {
            (document.getElementById('reactgooglegraph-1') as any).style.cursor = 'pointer';
          }
        }
        const onMouseOutHandler = (e: Event) => {
          if (document.getElementById('reactgooglegraph-1')) {
            (document.getElementById('reactgooglegraph-1') as any).style.cursor = 'default';
          }
        }
        const onClickHandler = (e: any) => {
          if (!isDrilldownData && userInfo?.IsAllOffices === "True" && [1, 2, 3].indexOf(formValues.reportTypeId) !== -1) {
            if (e.targetID) {
              const selection = e.targetID.split('#');
              if (selection[0].indexOf('vAxis') > -1) {
                const reportTypeName: string = reportTypeNameArr[formValues.reportTypeId];
                navigateWithFilter(reportTypeName, selection[selection.length - 1], -1);
              }
            }
          }
        }
        (google.visualization.events as any).removeListener(mouseOverListenerHandler);
        (google.visualization.events as any).removeListener(mouseOutListenerHandler);
        (google.visualization.events as any).removeListener(clickListenerHandler);
        mouseOverListenerHandler = (google.visualization.events as any).addListener(chart, 'onmouseover', onMouseOverHandler);
        mouseOutListenerHandler = (google.visualization.events as any).addListener(chart, 'onmouseout', onMouseOutHandler);
        clickListenerHandler = (google.visualization.events as any).addListener(chart, 'click', onClickHandler);
      }
    }
  ];

  const navigateWithFilter = (reportTypeName: string, selectedRow: number, selectedColumn: number) => {
    let filter: any = {
      [reportTypeName]: {
        ids: [reportData.graphData[selectedRow].id],
        isFocus: false,
        names: [reportData.graphData[selectedRow].name]
      },
    }
    if (reportData.legends.length > 0 && selectedColumn > -1) {
      let filterName = '';
      if (formValues.reportTypeId === 6 || formValues.reportTypeId === 7) {
        filterName = 'status';
      } else if (formValues.reportTypeId === 4) {
        filterName = 'support';
      } else if ([1, 2, 3].indexOf(formValues.reportTypeId) !== -1) {
        filterName = 'office';
      }
      if (filterName !== 'office') {
        const selectedLegendId = reportData.legends[selectedColumn].id;
        selectedColumn = _.findIndex(reportData.graphData[selectedRow].breakdown, { id: selectedLegendId });
      }
      if (reportData.graphData[selectedRow].breakdown.length > 0) {
        if (formValues.reportTypeId === 7 && reportData.graphData[selectedRow].breakdown[selectedColumn].id === 1) {
          // Skip filter for Total cases opened
        } else {
          filter[filterName] = {
            ids: [reportData.graphData[selectedRow].breakdown[selectedColumn].id],
            isFocus: false,
            names: [reportData.graphData[selectedRow].breakdown[selectedColumn].name]
          }
        }
      }
    }
    if (formValues.rangeTypeId !== 4 || (formValues.rangeTypeId === 4 && (formValues.fromDate !== null || formValues.toDate !== null))) {
      filter['dateRange'] = {
        fromDate: dateRanges[formValues.rangeTypeId].fromDate,
        isFocus: false,
        toDate: dateRanges[formValues.rangeTypeId].toDate
      }
    }
    localStorage.setItem('reportsFilter', JSON.stringify(formValues));
    localStorage.removeItem('dashboardFilter');
    navigate(`/dashboard?SearchFilters=${buildBase64String(filter)}`)
  }

  const buildBase64String = (filter: any) => {
    let base64String = '';
    try {
      var jsonString = JSON.stringify(filter);

      // Encode JSON string as Base64
      base64String = btoa(jsonString);
    }
    catch (error) {
      console.log(error);
    }
    return base64String;
  }

  const downloadFile = () => {
    setIsLoading(true);
    let payload = {} as any;
    let apiUrl = '';
    if (formValues.reportTypeId === 8) {
      apiUrl = '/Report/DownloadLoginUserDataGrid';
      Object.assign(payload, postData);
    } else if (formValues.reportTypeId === 9) {
      apiUrl = '/Report/DownloadUserActivityDataGrid';
      Object.assign(payload, postData);
    } else {
      apiUrl = '/Report/DownloadReport';
      Object.assign(payload, formValues);
      payload.masterData = null;
    }
    axiosInstance.post(apiUrl, payload, { responseType: 'arraybuffer' }).then((response: any) => {
      setIsLoading(false);
      if (response.status === 200) {
        utils.DownloadBlobExcelFileWithContentDisposition(response);
      }
    })
  }

  const handleSortChange = (field: string) => {
    let sortDesc = false;
    if (field === postData?.pagination.sortByField) {
      sortDesc = !postData?.pagination.sortOrderDesc;
    }
    setPostData(prevPostData => {
      if (prevPostData) {
        return {
          ...prevPostData,
          pagination: {
            ...prevPostData.pagination,
            pageNumber: 1,
            sortByField: field,
            sortOrderDesc: sortDesc
          }
        }
      }
    });
    setTriggerEffect(true);
  };

  const handleRecordsPerPageChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    setPostData(prevPostData => {
      if (prevPostData && typeof data.value === 'number') {
        return {
          ...prevPostData,
          pagination: {
            ...prevPostData.pagination,
            pageNumber: 1,
            recordsPerPage: data.value,
          }
        }
      }
    });
    setTriggerEffect(true);
  }

  const handlePaginationChange = (event: React.SyntheticEvent<HTMLAnchorElement>, data: PaginationProps) => {
    setPostData(prevPostData => {
      if (prevPostData && typeof data.activePage === 'number') {
        return {
          ...prevPostData,
          pagination: {
            ...prevPostData.pagination,
            pageNumber: data.activePage,
          }
        }
      }
    });
    setTriggerEffect(true);
  }

  const handleOfficeChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    setPostData(prevPostData => {
      if (prevPostData && (typeof data.value === 'number' || typeof data.value === 'string')) {
        return {
          ...prevPostData,
          officeId: data.value ? data.value : null,
          pagination: {
            ...prevPostData.pagination,
            pageNumber: 1,
          }
        }
      }
    });
    setTriggerEffect(true);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchText: string | null) => {
      setPostData(prevPostData => {
        if (prevPostData) {
          return {
            ...prevPostData,
            searchText,
            pagination: {
              ...prevPostData.pagination,
              pageNumber: 1,
            }
          }
        }
      });
      setTriggerEffect(true);
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tmpSearchText = event.target.value.trim() === '' ? null : event.target.value;
    setSearchText(tmpSearchText);
    debouncedSearch(tmpSearchText);
  };

  const handleClearSearch = () => {
    setSearchText('');
    debouncedSearch.cancel(); // Cancel any pending debounced search
    debouncedSearch(null);
  };

  const convertToDropDownProps = (options: Array<IdNameViewModel>) => {
    return options.map((item) => ({
      key: item.id,
      text: item.name,
      value: item?.id,
    }));
  }

  const recordsPerPageOptions = [
    { key: '5', text: '5', value: 5 },
    { key: '10', text: '10', value: 10 },
    { key: '20', text: '20', value: 20 },
    { key: '50', text: '50', value: 50 },
  ];

  const isMobileView = screenWidth < 991;

  const totalPages = dataGrid ? Math.ceil(dataGrid?.pagination?.totalCount / dataGrid?.pagination?.recordsPerPage) : 0;

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Reports</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className='section__body'>
        <Dimmer.Dimmable as="div" dimmed={isLoading}>
          <Dimmer active={isLoading} inverted={true}>
            <Loader>Loading</Loader>
          </Dimmer>
          <Grid padded>
            <Grid.Row>
              <Grid.Column mobile={16} tablet={3} computer={3}>
                <nav className="nav-sidebar">
                  <Menu compact icon="labeled" vertical>
                    {formValues?.masterData?.reportType.map((reportType, columnIndex) => {
                      return (
                        <Menu.Item
                          key={columnIndex}
                          data-name={reportType.name}
                          active={formValues.reportTypeId === reportType.id}
                          onClick={() => {
                            handleReportType(reportType.id);
                          }}
                          style={{
                            paddingLeft: '1.3rem'
                          }}
                        >
                          {reportType.name}
                        </Menu.Item>
                      );
                    })}
                  </Menu>
                </nav>
              </Grid.Column>
              <Grid.Column mobile={16} tablet={13} computer={13}>
                <nav className="nav-sidebar" style={{
                  padding: 0,
                  margin: 0
                }}>
                  <Menu compact icon="labeled" style={{
                    fontSize: '1.4rem'
                  }}>
                    {formValues?.masterData?.rangeType.map((rangeType, rangeIndex) => {
                      return (
                        <Menu.Item
                          key={rangeIndex}
                          data-name={rangeType.name}
                          active={formValues.rangeTypeId === rangeType.id}
                          onClick={() => {
                            handleRangeType(rangeType.id);
                          }}
                          style={{
                            paddingLeft: '1.3rem'
                          }}
                        >
                          {rangeType.name}
                        </Menu.Item>
                      );
                    })}
                  </Menu>
                  {isGraphDataAvailable && (
                    <div style={{ float: 'right' }}>
                      <Button className="btn-bright" onClick={() => downloadFile()}>
                        <Button.Content visible>
                          <ReactSVG
                            src={DocumentDownload}
                          />
                        </Button.Content>

                        <Button.Content visible>
                          Download Graph Data
                        </Button.Content>
                      </Button>
                    </div>
                  )}
                </nav>
                {formValues.rangeTypeId === dateRangeId && (
                  <div style={{
                    marginTop: '2rem'
                  }}>
                    <div style={{
                      float: 'left',
                      marginRight: '2rem'
                    }}>
                      <div className="field">
                        <label>From date</label>
                      </div>
                      <SemanticDatepicker
                        id="fromDate"
                        maxDate={new Date()}
                        format={'MM/DD/YYYY'}
                        onChange={(event, date) => handleDateChange(date, 'fromDate')}
                        value={formValues.fromDate ? new Date(formValues.fromDate) : undefined}
                      />
                    </div>
                    <div>
                      <div className="field">
                        <label>To date</label>
                      </div>
                      <SemanticDatepicker
                        id="toDate"
                        maxDate={new Date()}
                        format={'MM/DD/YYYY'}
                        onChange={(event, date) => handleDateChange(date, 'toDate')}
                        value={formValues.toDate ? new Date(formValues.toDate) : undefined}
                      />
                    </div>
                  </div>
                )}
                <Grid celled>
                  {formValues.reportTypeId <= 7 && (
                    <>
                      <Grid.Row>
                        <Grid.Column width={formValues.reportTypeId === reportTagId ? 11 : 13}>
                          <h3>{isDrilldownData && <Icon name='chevron left' className='cursor-pointer' onClick={revertDrilldownChart} />}{reportData.title}{isDrilldownData ? `: ${drilldownTitle}` : ''}</h3>
                        </Grid.Column>
                        {formValues.reportTypeId === reportTagId && (
                          <Grid.Column width={2} floated='right'>
                            <Checkbox
                              toggle
                              label="Support"
                              onChange={(e, data) => {
                                handleSupport(data.checked ? true : false);
                              }}
                              style={{
                                fontSize: '1.4rem',
                                marginTop: '0.5rem'
                              }}
                              checked={formValues.includeBreakDown ? true : false}
                            />
                          </Grid.Column>
                        )}
                        {formValues.reportTypeId !== 7 &&
                          <Grid.Column width={3} floated='right'>
                            <h4>Total: {reportData.totalCount}</h4>
                          </Grid.Column>
                        }
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16} style={{ paddingTop: 0 }}>
                          {!isLoading && graphData && graphData.length > 0 && (
                            <Chart
                              chartType="BarChart"
                              height={chartHeight}
                              data={graphData}
                              options={graphOptions}
                              chartEvents={chartEvents}
                              key={Math.random()}
                            />
                          )}
                          {isGraphDataAvailable === false && (
                            <div className="ui error message" style={{ marginTop: '1.5rem' }}>No data available.</div>
                          )}
                        </Grid.Column>
                      </Grid.Row>
                      {formValues.reportTypeId === reportTagId && (
                        <Grid.Row>
                          <Grid.Column width={2} floated='right'>
                            <Checkbox
                              toggle
                              label="Support"
                              onChange={(e, data) => {
                                handleSupport(data.checked ? true : false);
                              }}
                              style={{
                                fontSize: '1.4rem'
                              }}
                              checked={formValues.includeBreakDown ? true : false}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      )}
                    </>
                  )}
                  {formValues.reportTypeId > 7 && (
                    <>
                      <Grid.Row>
                        <Grid.Column width={13}>
                          {formValues.reportTypeId === 8 && <h3>Log-in Report</h3>}
                          {formValues.reportTypeId === 9 && <h3>User Activity Report</h3>}
                        </Grid.Column>
                        {formValues.reportTypeId !== 9 &&
                          <Grid.Column width={3} floated='right'>
                            <h4>Total: {dataGrid?.pagination?.totalCount}</h4>
                          </Grid.Column>
                        }
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column mobile={isMobileView ? 16 : 5}>
                          <span style={{ marginRight: '1rem' }}>Office</span>
                          <Dropdown
                            selection
                            clearable
                            placeholder='All'
                            onChange={handleOfficeChange}
                            value={dataGrid?.officeId ? dataGrid.officeId : ''}
                            options={convertToDropDownProps(dataGrid?.master?.office || [])}
                          />
                        </Grid.Column>
                        <Grid.Column mobile={isMobileView ? 16 : 11}>
                          <Grid>
                            <Grid.Row>
                              <Grid.Column width={10} floated={isMobileView ? "left" : "right"}>
                                <div className='position-relative'>
                                  <Input
                                    fluid
                                    value={searchText ? searchText : ''}
                                    icon="search"
                                    iconPosition="left"
                                    placeholder='Search by first name or last name'
                                    onChange={handleSearchChange}
                                  />
                                  {searchText && searchText.length >= 1 && (
                                    <Icon name="times" link onClick={handleClearSearch} style={{ position: 'absolute', right: '2rem', top: '1.1rem' }} />
                                  )}
                                </div>
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16} style={{ paddingTop: 0 }}>
                          <div className="table-holder">
                            {dataGrid && dataGrid?.searchResults?.length > 0 && (
                              <Table sortable={true} striped={true}>
                                <Table.Header>
                                  <Table.Row>
                                    {columns.map((column: any, index: number) => (
                                      <Table.HeaderCell
                                        key={index}
                                        sorted={column.field === dataGrid?.pagination.sortByField ? (dataGrid?.pagination.sortOrderDesc ? 'descending' : 'ascending') : undefined}
                                        onClick={column.sortable ? () => handleSortChange(column.field as string) : undefined}
                                        style={column.headerStyle}
                                      >
                                        {column.header}
                                        {column.sortable ? <span className="table__sort">
                                          {column.field === dataGrid?.pagination.sortByField ? (dataGrid?.pagination.sortOrderDesc ? undefined : undefined) : <Icon name="sort" />}
                                        </span> : null}
                                      </Table.HeaderCell>
                                    ))}
                                  </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                  {dataGrid.searchResults.map((row: any, index: number) => (
                                    <Table.Row key={`report-${index}`}>
                                      {columns.map((column: any, index: number) => (
                                        <Table.Cell key={index} className={column.className}>{column.accessor(row)}</Table.Cell>
                                      ))}
                                    </Table.Row>
                                  ))}
                                </Table.Body>
                                <Table.Footer>
                                  <Table.Row>
                                    <Table.HeaderCell colSpan={Math.floor(columns.length / 2)}>
                                      <div className='select-pagination'>
                                        <span>Show</span>
                                        <div className='select__inner'>
                                          <Dropdown
                                            selection
                                            options={recordsPerPageOptions}
                                            value={dataGrid?.pagination.recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                          />
                                        </div>
                                        <div className='total-block'>Total {dataGrid?.pagination.totalCount}</div>
                                      </div>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell colSpan={Math.ceil(columns.length / 2)} floated='right'>
                                      {totalPages > 0 &&
                                        <Pagination
                                          className='floated right'
                                          activePage={dataGrid?.pagination.pageNumber}
                                          onPageChange={handlePaginationChange}
                                          totalPages={totalPages}
                                          siblingRange={isMobileView ? 1 : 2}
                                          boundaryRange={isMobileView ? 0 : 1}
                                          firstItem={
                                            (dataGrid?.pagination.pageNumber > 1 && !isMobileView) ?
                                              {
                                                content: <Icon name="angle double left" />,
                                                icon: true
                                              }
                                              : null
                                          }
                                          lastItem={
                                            (dataGrid?.pagination.pageNumber !== totalPages && !isMobileView) ?
                                              {
                                                content: <Icon name="angle double right" />,
                                                icon: true,
                                              }
                                              : null
                                          }
                                          prevItem={{
                                            content: <Icon name="angle left" />,
                                            icon: true,
                                          }}
                                          nextItem={{
                                            content: <Icon name="angle right" />,
                                            icon: true,
                                          }}
                                          ellipsisItem={{
                                            content: <Icon name="ellipsis horizontal" />,
                                            icon: true,
                                          }}
                                        />
                                      }
                                    </Table.HeaderCell>
                                  </Table.Row>
                                </Table.Footer>
                              </Table>
                            )}
                          </div>
                          {dataGrid?.searchResults?.length === 0 && (
                            <div style={{ textAlign: 'center' }}>
                              <h3>No records found.</h3>
                            </div>
                          )}
                        </Grid.Column>
                      </Grid.Row>
                    </>
                  )}
                </Grid>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Dimmer.Dimmable>
      </div>
    </section>
  );
}

export default Reports;